import { readFileSync } from 'node:fs'
import { api, formatError } from '../api-client'
import { flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { suggestFromSet } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Broker CLI — OPERATE existing Connections through governed named Operations.
// Credentials are resolved server-side (they never reach the CLI) and every call
// is audited; reads AND writes run inline (Driftless is a tooling proxy, the
// human-in-the-loop is your own agent harness).
//
// F7.1 — two SEPARATE concerns, kept apart on purpose:
//   • INTEGRATION SETUP — connecting/configuring a provider (catalog → connect →
//     confirm → disconnect). This is the privileged connect flow; it lives in the
//     dashboard (Settings → Connections) and the integrations API
//     (POST integrations/nango/:provider/connect-session → authorize → /confirm).
//     `broker setup` prints exactly where to do it.
//   • BROKER EXECUTION — using a connection that already exists: connections,
//     operations, invoke, records, events, criterion. That is everything BELOW.
// Authoring/deploying a Nango action is a third, human-only lane the broker never
// touches (no-agent-scripting rule, F7.0).

const KNOWN_BROKER_FLAGS: ReadonlySet<string> = new Set(['input', 'add', 'rm', 'model', 'modified-after', 'cursor', 'limit', 'refresh', 'query', 'effect', 'risk', 'provider', 'max-bytes', 'sample-limit', 'import-run-id', 'dry-run', 'json', 'help'])

export function assertKnownBrokerFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_BROKER_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    const hint = suggestFromSet(f, KNOWN_BROKER_FLAGS)
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless broker':\n` +
      lines.join('\n') +
      `\nRun 'driftless broker --help' for valid flags.`,
  )
}

// Pure row formatters — extracted from the command switch so they're unit-tested
// (the surface an agent/human reads). These render the CURRENT broker shapes:
// a connection {provider, connected, criterion}, an operation {name, effect,
// description}, a pending write {id, action_name, status, provider}. Keep them in
// lockstep with the API response — a stale field here renders "undefined" to the
// user (the exact bug that slipped when the proxy model {method,endpoint} was
// dropped for the actions model {action_name}).
export function formatConnectionRow(c: Record<string, unknown>): string {
  const crit = (c['criterion'] as string[]) ?? []
  // P2 — the API derives a plain-language sync status + next action (one source); we
  // just render it, so the CLI and dashboard never disagree on the wording.
  const status = (c['status'] as { label?: string; next_action?: string } | undefined) ?? undefined
  const head = `${String(c['provider']).padEnd(20)} ${c['connected'] ? 'connected' : 'not connected'}${crit.length ? `  · criterion: ${crit.join(', ')}` : ''}`
  if (!status?.label) return head
  const lines = [head, `  ${status.label}`]
  if (status.next_action) lines.push(`  → ${status.next_action}`)
  return lines.join('\n')
}

// P4/P8 — render a Notion page-content read: the citeable header + the bounded text,
// with a clear note when content was truncated or block types were unsupported.
export function formatPageContent(p: Record<string, unknown>): string {
  const lines: string[] = []
  const title = typeof p['title'] === 'string' && p['title'] ? (p['title'] as string) : '(untitled)'
  lines.push(`${title}  ·  ${p['page_id'] ?? ''}`)
  if (p['url']) lines.push(String(p['url']))
  if (p['last_edited_time']) lines.push(`last edited: ${p['last_edited_time']}`)
  const unsupported = Array.isArray(p['unsupported']) ? (p['unsupported'] as unknown[]) : []
  if (unsupported.length) lines.push(`(omitted unsupported blocks: ${unsupported.join(', ')})`)
  if (p['truncated']) lines.push('(truncated — bounded prefix, the page is larger)')
  lines.push('')
  lines.push(typeof p['text'] === 'string' ? (p['text'] as string) : '')
  return lines.join('\n')
}

export function formatCapabilityDirectory(d: Record<string, unknown>): string {
  const caps = Array.isArray(d['capabilities']) ? (d['capabilities'] as Array<Record<string, unknown>>) : []
  const provider = String(d['display_name'] ?? d['provider'] ?? 'Provider')
  const connection = (d['connection'] && typeof d['connection'] === 'object' && !Array.isArray(d['connection']))
    ? d['connection'] as Record<string, unknown>
    : {}
  const connectionStatus = (connection['status'] && typeof connection['status'] === 'object' && !Array.isArray(connection['status']))
    ? connection['status'] as Record<string, unknown>
    : {}
  const ready = caps.filter((c) => ((c['status'] as Record<string, unknown> | undefined)?.['state']) === 'ready')
  const attention = caps.filter((c) => {
    const state = (c['status'] as Record<string, unknown> | undefined)?.['state']
    return state && state !== 'ready' && !(c['kind'] === 'write_policy' && state === 'disabled')
  })
  const writesDisabled = caps.some((c) => c['kind'] === 'write_policy' && ((c['status'] as Record<string, unknown> | undefined)?.['state']) === 'disabled')

  const lines: string[] = [`${provider} capabilities`]
  if (connectionStatus['label']) lines.push(`  ${connectionStatus['label']}`)
  if (ready.length) {
    lines.push('', 'Ready')
    for (const c of ready) {
      const read = c['read'] && typeof c['read'] === 'object' && !Array.isArray(c['read']) ? c['read'] as Record<string, unknown> : null
      lines.push(`  ✓ ${c['name']} [${c['kind']}]${read?.['api'] ? ` — ${read['api']}` : ''}`)
    }
  }
  if (attention.length) {
    lines.push('', 'Needs attention')
    for (const c of attention) {
      const status = (c['status'] as Record<string, unknown> | undefined) ?? {}
      const rawState = String(status['state'] ?? 'unknown')
      const state = rawState === 'deploy_pending' ? 'setup_pending' : rawState === 'deploy_failed' ? 'setup_failed' : rawState
      const label = status['label'] ? ` — ${status['label']}` : ''
      const next = status['next_action'] ? ` → ${status['next_action']}` : ''
      lines.push(`  • ${c['name']} [${state}]${label}${next}`)
    }
  }
  if (writesDisabled) lines.push('', 'Writes disabled for this connector.')
  const next = typeof d['next_action'] === 'string' ? d['next_action'] : connectionStatus['next_action']
  if (next) lines.push('', `Next: ${next}`)
  return lines.join('\n')
}

export function formatOperationRow(o: Record<string, unknown>): string {
  return `${String(o['name']).padEnd(20)} [${o['effect']}] ${o['description'] ?? ''}`.trimEnd()
}

// Cross-provider search hit — prefixes the provider (the search spans connections)
// and shows the criterion to read first, so a human scanning intent results knows
// which connection AND what governs it.
export function formatOperationSearchRow(o: Record<string, unknown>): string {
  const crit = (o['criterion'] as string[]) ?? []
  const fields = (o['input_fields'] as string[]) ?? []
  return `${String(o['provider']).padEnd(14)} ${String(o['name']).padEnd(20)} [${o['effect']}] ${o['description'] ?? ''}`.trimEnd()
    + (fields.length ? `\n    input: ${fields.join(', ')}` : '')
    + (crit.length ? `\n    criterion: ${crit.join(', ')}` : '')
}

// P3.0 — INSPECT one operation: the full spec a human/agent reads before invoking
// (schema, governance overlay, what to read first, the bounded output budget). A
// multi-line block, not a row — this is the "explain" surface.
export function formatOperationInspect(o: Record<string, unknown>): string {
  const lines: string[] = []
  lines.push(`${o['provider']} ${o['name']}  [${o['effect']}]${o['description'] ? `  ${o['description']}` : ''}`)
  const risk = o['risk'] ? String(o['risk']) : '—'
  const idem = o['idempotent'] === true ? 'yes' : o['idempotent'] === false ? 'no' : '—'
  lines.push(`  policy: ${o['policy_status'] ?? '—'} · risk: ${risk} · idempotent: ${idem}${o['rollout'] ? ` · rollout: ${o['rollout']}` : ''}`)
  const scopes = (o['scopes'] as string[]) ?? []
  if (scopes.length) lines.push(`  scopes: ${scopes.join(', ')}`)
  const crit = (o['criterion'] as string[]) ?? []
  if (o['requires_criterion'] || crit.length) lines.push(`  criterion: ${crit.length ? crit.join(', ') : '(none attached)'}${o['requires_criterion'] ? ' (required)' : ''}`)
  if (o['stale']) lines.push(`  ⚠ metadata stale — refresh: driftless broker operations ${o['provider']} --refresh`)
  const fields = inputFieldNames(o['input'])
  if (fields.length) lines.push(`  input: ${fields.join(', ')}`)
  if (o['next_action']) lines.push(`\n${o['next_action']}`)
  return lines.join('\n')
}

// Top-level field names of an operation input schema (flat {field:type} or a
// {properties:{…}} JSON-schema), bounded — mirrors the API's summary so inspect
// shows what to pass without dumping the whole schema.
function inputFieldNames(input: unknown): string[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return []
  const obj = input as Record<string, unknown>
  const src = obj['properties'] && typeof obj['properties'] === 'object' ? (obj['properties'] as Record<string, unknown>) : obj
  return Object.keys(src).slice(0, 30)
}

// P3.1 — an importable record model (a Nango sync's output) the agent can read and
// map into a Collection. Shows the producing sync + cadence so "what can I import?"
// is answerable before choosing a model.
export function formatModelRow(m: Record<string, unknown>): string {
  const freq = m['frequency'] ? `  every ${m['frequency']}` : ''
  return `${String(m['model']).padEnd(20)} ← sync ${m['sync']}${freq}${m['description'] ? `  · ${m['description']}` : ''}`.trimEnd()
}

export function formatIndexResult(r: Record<string, unknown>): string {
  const mode = r['mode'] === 'execute' ? 'index execute' : 'index preview'
  const provider = String(r['provider'] ?? 'provider')
  const model = String(r['model'] ?? 'model')
  const lines: string[] = [`${provider} ${mode} · ${model}`]
  lines.push(`  scanned: ${r['scanned'] ?? 0} · candidates: ${r['candidate_count'] ?? 0}${r['candidate_count_is_exact'] === false ? '+' : ''}`)
  if (r['mode'] === 'execute') {
    lines.push(`  indexed: ${r['indexed'] ?? 0} · created: ${r['created'] ?? 0} · updated: ${r['updated'] ?? 0} · skipped: ${r['skipped'] ?? 0}`)
  } else {
    lines.push(`  estimated: ${r['estimated_bytes'] ?? 0} bytes · ${r['estimated_tokens'] ?? 0} tokens`)
    const samples = Array.isArray(r['samples']) ? (r['samples'] as Array<Record<string, unknown>>) : []
    if (samples.length) {
      lines.push('', 'Samples')
      for (const s of samples) {
        lines.push(`  • ${s['title'] || '(untitled)'} · ${s['external_id']}${s['truncated'] ? ' · truncated' : ''}`)
      }
    }
  }
  const skipped = Array.isArray(r['skipped_reasons']) ? r['skipped_reasons'] : Array.isArray(r['skipped']) ? r['skipped'] : []
  if (skipped.length) {
    lines.push('', 'Skipped')
    for (const s of skipped as Array<Record<string, unknown>>) lines.push(`  • ${s['reason']}: ${s['count']}`)
  }
  if (r['next_action']) lines.push('', `Next: ${r['next_action']}`)
  return lines.join('\n')
}

export function formatEventRow(e: Record<string, unknown>): string {
  return `${String(e['created_at'] ?? '').slice(0, 19)}  ${String(e['type']).padEnd(10)} (${e['provider'] ?? '—'})`
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so BROKER_HELP still resolves from './broker'.
export { BROKER_HELP } from './help-text'
import { BROKER_HELP } from './help-text'

// F7.1 — the integration-SETUP guide. The broker can't connect a provider (that's
// the privileged connect flow); this names exactly where setup happens so the two
// concerns never get conflated. Exported as both prose and a machine shape so it's
// unit-tested and --json-able.
export const BROKER_SETUP_GUIDE = `Connecting a provider is integration SETUP — a separate step from using the broker.

1. Dashboard (recommended): Settings → Connections → pick a provider → authorize.
2. API connect flow (programmatic):
     GET  workspaces/<ws>/integrations/nango/catalog            (connectable providers)
     POST workspaces/<ws>/integrations/nango/<provider>/connect-session   (start)
     …authorize in the returned Connect UI…
     POST workspaces/<ws>/integrations/nango/<provider>/confirm  (persist + enable actions)
   Disconnect: DELETE workspaces/<ws>/integrations/<id>

Once connected it shows in 'driftless broker connections', and you OPERATE it with
'broker operations' / 'broker invoke' / 'broker records'. The broker itself never
connects, disconnects, or authors Nango scripts.`

export function brokerSetupGuide(): {
  catalog: string
  connectSession: string
  confirm: string
  disconnect: string
  note: string
} {
  return {
    catalog: 'GET workspaces/<ws>/integrations/nango/catalog',
    connectSession: 'POST workspaces/<ws>/integrations/nango/<provider>/connect-session',
    confirm: 'POST workspaces/<ws>/integrations/nango/<provider>/confirm',
    disconnect: 'DELETE workspaces/<ws>/integrations/<id>',
    note: 'Integration setup is separate from broker execution. The broker only operates connections that already exist.',
  }
}

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(BROKER_HELP)
}

function parseJsonFlag(raw: string | boolean | undefined, label: string): Record<string, unknown> | undefined {
  if (raw === undefined || raw === true || raw === false) return undefined
  const text = raw.startsWith('@') ? readFileSync(raw.slice(1), 'utf-8') : raw
  try {
    const obj = JSON.parse(text)
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) throw new Error('not an object')
    return obj as Record<string, unknown>
  } catch {
    console.error(`Error: --${label} must be a JSON object (inline or @file).`)
    process.exit(1)
  }
}

export async function brokerCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownBrokerFlags(flags)

  if (sub === 'capabilities' && !positional[1]) {
    console.error("Error: 'broker capabilities' needs a <provider>.")
    process.exit(1)
  }

  // F7.1 — 'setup' is pure guidance (the integration-setup flow), needs no
  // workspace and makes no API call; handle it before resolving anything.
  if (sub === 'setup') {
    if (isJSON) emitJSON(brokerSetupGuide())
    else console.log(BROKER_SETUP_GUIDE)
    process.exit(0)
  }

  const ws = await resolveWorkspaceSlug()

  try {
    switch (sub) {
      case 'connections': {
        const rows = (await api.get(`/workspaces/${ws}/broker/connections`)) as Array<Record<string, unknown>>
        if (isJSON) { emitJSON(rows); break }
        if (!rows.length) { console.log("No connections yet. Connecting a provider is integration SETUP, not a broker command — run 'driftless broker setup' for the steps (Settings → Connections)."); break }
        for (const c of rows) console.log(formatConnectionRow(c))
        break
      }
      case 'capabilities': {
        const provider = positional[1]
        const res = (await api.get(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/capabilities`)) as Record<string, unknown>
        if (isJSON) { emitJSON(res); break }
        console.log(formatCapabilityDirectory(res))
        break
      }
      case 'criterion': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'broker criterion' needs a <provider>."); process.exit(1) }
        const add = typeof flags['add'] === 'string' ? flags['add'] : null
        const rm = typeof flags['rm'] === 'string' ? flags['rm'] : null
        if (add) {
          const r = (await api.post(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/criterion`, { topic: add })) as Record<string, unknown>
          if (isJSON) { emitJSON(r); break }
          console.log(`Attached. criterion: ${((r['criterion'] as string[]) ?? []).join(', ') || '(none)'}`)
        } else if (rm) {
          const r = (await api.delete(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/criterion/${encodeURIComponent(rm)}`)) as Record<string, unknown>
          if (isJSON) { emitJSON(r); break }
          console.log(`Detached. criterion: ${((r['criterion'] as string[]) ?? []).join(', ') || '(none)'}`)
        } else {
          const rows = (await api.get(`/workspaces/${ws}/broker/connections`)) as Array<Record<string, unknown>>
          const conn = rows.find((c) => c['provider'] === provider)
          const crit = (conn?.['criterion'] as string[]) ?? []
          if (isJSON) { emitJSON({ provider, criterion: crit }); break }
          console.log(crit.length ? `criterion for ${provider}: ${crit.join(', ')}` : `No criterion attached to ${provider}. Add one: driftless broker criterion ${provider} --add <topic-slug>`)
        }
        break
      }
      case 'operations': {
        const provider = positional[1] ?? (typeof flags['provider'] === 'string' ? flags['provider'] : undefined)
        const query = typeof flags['query'] === 'string' ? flags['query'].trim() : ''
        // Cross-provider DISCOVERY by intent (mirrors MCP): a --query, or no
        // provider, searches every connection so you never list-all-and-guess at
        // scale. provider + no query keeps the per-connection list (back-compat).
        if (query || !provider) {
          const qs = new URLSearchParams()
          if (query) qs.set('query', query)
          if (provider) qs.set('provider', provider)
          if (typeof flags['effect'] === 'string') qs.set('effect', flags['effect'])
          if (typeof flags['risk'] === 'string') qs.set('risk', flags['risk'])
          if (typeof flags['limit'] === 'string') qs.set('limit', flags['limit'])
          if (typeof flags['cursor'] === 'string') qs.set('cursor', flags['cursor'])
          const res = (await api.get(`/workspaces/${ws}/broker/operations?${qs.toString()}`)) as Record<string, unknown>
          if (isJSON) { emitJSON(res); break }
          const ops = (res['operations'] as Array<Record<string, unknown>>) ?? []
          if (!ops.length) { console.log(`No operation matches "${query || '(any)'}". Broaden the query, drop --provider/--effect, or check 'driftless broker connections'.`); break }
          for (const o of ops) console.log(formatOperationSearchRow(o))
          const top = ops[0]
          console.log(`\nInvoke: driftless broker invoke ${top['provider']} ${top['name']} --input '{…}'${res['nextCursor'] ? `   (more: --cursor ${res['nextCursor']})` : ''}`)
          break
        }
        // F7.2 — served from materialized metadata; --refresh re-pulls from Nango.
        const refresh = flags['refresh'] === true || flags['refresh'] === 'true'
        const q = refresh ? '?refresh=true' : ''
        const rows = (await api.get(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/operations${q}`)) as Array<Record<string, unknown>>
        if (isJSON) { emitJSON(rows); break }
        if (!rows.length) { console.log(`No operations for ${provider}. Enable actions for this integration in Nango, then run 'driftless broker operations ${provider} --refresh'.`); break }
        for (const o of rows) console.log(formatOperationRow(o))
        // P2.0 — the criterion→action seam: a write/criterion op should be preceded
        // by reading the connection's governing criterion topics.
        if (rows.some((o) => o['requires_criterion'] === true || o['effect'] === 'write')) {
          console.log(`\nBefore a WRITE/risky op, read the connection's criterion: driftless broker criterion ${provider}  →  driftless context get <slug>`)
        }
        break
      }
      case 'inspect': {
        const provider = positional[1]
        const operation = positional[2]
        if (!provider || !operation) { console.error("Error: 'broker inspect' needs <provider> <operation>."); process.exit(1) }
        const o = (await api.get(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/operations/${encodeURIComponent(operation)}`)) as Record<string, unknown>
        if (isJSON) { emitJSON(o); break }
        console.log(formatOperationInspect(o))
        break
      }
      case 'invoke': {
        const provider = positional[1]
        const operation = positional[2]
        if (!provider || !operation) { console.error("Error: 'broker invoke' needs <provider> <operation>."); process.exit(1) }
        const input = parseJsonFlag(flags['input'], 'input')
        const body: Record<string, unknown> = { operation }
        if (input) body['input'] = input
        const result = (await api.post(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/invoke`, body)) as Record<string, unknown>
        if (isJSON) { emitJSON(result); break }
        // Reads and writes both execute inline — print the provider's response.
        console.log(JSON.stringify(result['result'], null, 2))
        break
      }
      case 'models': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'broker models' needs a <provider>."); process.exit(1) }
        const res = (await api.get(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/models`)) as Record<string, unknown>
        if (isJSON) { emitJSON(res); break }
        const models = (res['models'] as Array<Record<string, unknown>>) ?? []
        if (!models.length) { console.log(`No importable models for ${provider}. Enable a sync for this integration in Nango, then re-check.`); break }
        for (const m of models) console.log(formatModelRow(m))
        if (res['next_action']) console.log(`\n${res['next_action']}`)
        break
      }
      case 'records': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'broker records' needs a <provider>."); process.exit(1) }
        const model = typeof flags['model'] === 'string' ? flags['model'] : ''
        if (!model) { console.error("Error: 'broker records' needs --model <name>."); process.exit(1) }
        const qs = new URLSearchParams({ model })
        if (typeof flags['modified-after'] === 'string') qs.set('modifiedAfter', flags['modified-after'])
        if (typeof flags['cursor'] === 'string') qs.set('cursor', flags['cursor'])
        if (typeof flags['limit'] === 'string') qs.set('limit', flags['limit'])
        const result = (await api.get(`/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/records?${qs.toString()}`)) as Record<string, unknown>
        if (isJSON) { emitJSON(result); break }
        const records = (result['records'] as unknown[]) ?? []
        console.log(`${records.length} ${model} record(s)${result['nextCursor'] ? ` · next cursor: ${result['nextCursor']}` : ''}`)
        console.log(JSON.stringify(records, null, 2))
        break
      }
      case 'page': {
        // P4/P8 — read ONE Notion page's bounded, citeable content (read-only).
        const pageId = positional[1]
        if (!pageId) { console.error("Error: 'broker page' needs a <pageId> (a Notion ContentMetadata page id)."); process.exit(1) }
        const qs = new URLSearchParams()
        if (typeof flags['max-bytes'] === 'string') qs.set('maxBytes', flags['max-bytes'])
        const q = qs.toString()
        const res = (await api.get(`/workspaces/${ws}/broker/connections/notion/pages/${encodeURIComponent(pageId)}/content${q ? `?${q}` : ''}`)) as Record<string, unknown>
        if (isJSON) { emitJSON(res); break }
        console.log(formatPageContent(res))
        break
      }
      case 'index': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'broker index' needs a <provider>."); process.exit(1) }
        const body: Record<string, unknown> = {}
        if (typeof flags['model'] === 'string') body['model'] = flags['model']
        if (typeof flags['modified-after'] === 'string') body['modifiedAfter'] = flags['modified-after']
        if (typeof flags['limit'] === 'string') body['limit'] = Number(flags['limit'])
        if (typeof flags['sample-limit'] === 'string') body['sampleLimit'] = Number(flags['sample-limit'])
        if (typeof flags['max-bytes'] === 'string') body['maxBytes'] = Number(flags['max-bytes'])
        if (typeof flags['import-run-id'] === 'string') body['importRunId'] = flags['import-run-id']
        const dryRun = flags['dry-run'] === true || flags['dry-run'] === 'true'
        const path = `/workspaces/${ws}/broker/connections/${encodeURIComponent(provider)}/index${dryRun ? '/preview' : ''}`
        const res = (await api.post(path, body)) as Record<string, unknown>
        if (isJSON) { emitJSON(res); break }
        console.log(formatIndexResult(res))
        break
      }
      case 'events': {
        const provider = positional[1] // optional
        const qs = new URLSearchParams()
        if (provider) qs.set('provider', provider)
        if (typeof flags['limit'] === 'string') qs.set('limit', flags['limit'])
        const q = qs.toString()
        const rows = (await api.get(`/workspaces/${ws}/broker/events${q ? `?${q}` : ''}`)) as Array<Record<string, unknown>>
        if (isJSON) { emitJSON(rows); break }
        if (!rows.length) { console.log('No events yet.'); break }
        for (const e of rows) console.log(formatEventRow(e))
        break
      }
      default:
        console.error(`Unknown broker subcommand: '${sub}'. Run 'driftless broker --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: unknown) {
    console.error(formatError(e as Error))
    await flushAndExit(1)
  }
}
